import React, {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { I18n } from 'react-redux-i18n';
import { Link } from 'react-router-dom';
import { scrollSpy } from 'react-scroll';
import ReactMapGL from 'react-map-gl';
import styled from 'styled-components';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import some from 'lodash/some';
import reduce from 'lodash/reduce';

import Button from 'components/Button';
import Icon from 'components/Icon';
import Spinner from 'components/Spinner';
import Select from '../components/Select';
import CompareLineGraph from 'modules/SynthesisDistribution/components/CompareLineGraph';
import SelectDetail from 'modules/SynthesisDistribution/components/SelectDetail';
import TableComponent from 'modules/SynthesisDistribution/components/TableComponent';

import { useUserMe } from 'apiTyped/hooks/users.query';
import {
  getMarketsForRegion,
  useDistributionSynthesisFilters,
  useDistributionSynthesisQuery
} from 'apiTyped/hooks/distribution.query';
import toOptionsList from 'utils/valuesToOptions';

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  z-index: 1;
  animation-duration: 1s;
  animation-name: appear;
  overflow: hidden;
  position: relative;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    flex-direction: column;
  }

  .synthesisDetailMapContainer {
    width: 30%;
    z-index: 2;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      width: calc(100vw);
      height: 15rem;
    }
  }

  .mapboxgl-map {
    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
    }
  }

  .synthesisDetailOverlay {
    position: relative;
    height: 100vh;
    z-index: 3 !important;
    background-color: rgba(0, 0, 0, 0.3);
    color: white;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      position: absolute;
      top: 0;
      height: 15rem;
    }
  }

  .backToMap {
    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      display: none;
    }
  }

  .filtersContainer {
    display: flex;
    flex-direction: column;
    gap: 3rem;
    padding: 3rem;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      gap: 1.5rem;
      padding: 1.5rem;
      width: calc(100vw - 3rem);
    }
  }

  .label {
    font-weight: 600;
    color: #e25c32;
    font-size: 0.875rem;
  }

  a {
    text-decoration: none;
  }

  .rt-th {
    background-color: rgba(0, 0, 0, 0.01);
  }

  .ReactTable.-striped .rt-tr.-odd {
    background: rgba(0, 0, 0, 0) !important;
  }

  .rt-tr.-even {
    background: rgba(0, 0, 0, 0.03) !important;
  }

  .ReactTable .rt-tbody {
    pointer-events: none;
  }

  .comeBackContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 1rem;
    color: white;
    padding: 3rem;

    .comeBackIcon {
      font-size: 1.5rem;
    }

    h3 {
      margin-left: 1.35rem;
      text-transform: uppercase;
    }
  }

  .comeBackContainer:hover {
    opacity: 0.8;
  }

  .mapboxgl-ctrl-bottom-right {
    display: none !important;
  }

  .mapboxgl-ctrl-logo {
    display: none !important;
  }

  .synthesisContentContainer {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: white;
    color: #1c252f;
    max-height: 100vh;
    overflow-y: hidden;
    z-index: 1;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      width: 100vw;
      max-height: unset;
    }
  }

  .synthesisContentTitle {
    display: grid;
    grid-template-columns: 1fr 2rem;
    gap: 1.25rem;
    width: 100%;
    align-items: flex-end;
    color: rgb(28, 37, 47);

    h2 {
      font-family: ${(props) => props.theme.fonts.oswald};
      font-size: 1.5rem;
      text-transform: uppercase;
      color: black;
    }
  }

  .titleContainer {
    border-bottom: solid 1px rgba(108, 97, 97, 0.3);
  }

  .synthesisDetailHeader {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 4rem;
    padding: 2rem 2rem 0.75rem;
    border-bottom: solid 1px rgba(108, 97, 97, 0.3);

    @media (max-width: 1200px) {
      padding: 1.5rem 1.5rem 0.5rem;
    }

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      padding: 1rem 1rem 0.5rem;
    }
  }

  .synthesisDetailRightHeader {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 2rem;
  }

  .rankLabel {
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 1.5rem;
    text-transform: uppercase;
  }

  .rankValue {
    font-family: ${(props) => props.theme.fonts.silkserif_regular};
    font-size: 8rem;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      font-size: 4rem;
    }
  }

  .wsLabel {
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 0.875rem;
    color: #e25c32;
    text-transform: uppercase;
  }

  .wsValue {
    font-family: ${(props) => props.theme.fonts.silkserif_regular};
    font-size: 1.5rem;
    color: #1b242c;
  }

  .recharts-cartesian-axis-tick-value {
    font-family: ${(props) => props.theme.fonts.sfcompact};
    fill: #1f2b37;
    font-size: 0.75rem;
  }

  .wsContainer {
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      display: none;
    }
  }

  .synthesisDetailLeftHeader {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .synthesisContent {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 2rem 2rem 3rem;
    overflow-y: auto;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      gap: 1.5rem;
      padding: 1rem 1rem 10rem;
      overflow-y: hidden;
    }
  }

  .syntheseEvolution {
    background-color: white;
  }

  .indicatorContainer {
    height: 4rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .indicatorContainer:not(:last-child) {
    border-bottom: solid 1px rgba(108, 97, 97, 0.3);
  }

  .indicatorLabel {
    text-transform: uppercase;
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 0.875rem;
    color: #1a222c;
  }

  .indicatorValue {
    font-family: ${(props) => props.theme.fonts.silkserif_regular};
    font-size: 1.2rem;
    color: #1b242c;
    text-align: end;

    span {
      display: inline-block;
    }

    .spanNameLeader {
      font-size: 1rem;
    }
  }

  .synthesisContentIndicators {
    display: flex;
    align-items: center;
    gap: 2rem;

    @media (max-width: 1200px) {
      flex-direction: column;
      gap: 1rem;
    }
  }

  .bigIndicator {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    justify-content: center;
    align-items: center;
    width: 13rem;
    height: 13rem;
    min-width: 13rem;
    min-height: 13rem;
    background-color: #d55d3f;
    color: white;
    border-radius: 50%;

    @media (max-width: 1400px) {
      width: 11.5rem;
      height: 11.5rem;
      min-width: 11.5rem;
      min-height: 11.5rem;
    }
  }

  .bigIndicatorLabel {
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 1rem;
    text-align: center;
    text-transform: uppercase;

    @media (max-width: 1400px) {
      font-size: 0.75rem;
    }
  }

  .bigIndicatorValue {
    font-family: ${(props) => props.theme.fonts.silkserif_regular};
    font-size: 4rem;
    text-align: center;

    @media (max-width: 1400px) {
      font-size: 3rem;
    }
  }

  .synthesisContentIndicatorsLeft {
    display: flex;
    gap: 1rem;
    flex-direction: row;
    align-items: center;
  }

  .synthesisContentIndicatorsRight {
    width: 100%;
  }

  .actionButtonContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .graphTopText {
    font-family: ${(props) => props.theme.fonts.oswald_light};
    font-size: 0.75rem;
    line-height: 2.15;
    color: #1a222c;
  }

  .graphTopTextContainer {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;
  }
`;

const IconButton = styled(Button).attrs((props) => ({
  type: 'round',
  iconColor: props.theme.colors['white'],
  color: '#d55d3f',
  size: '50px'
}))`
  :hover {
    opacity: 0.8;
  }
`;

const SelectComponent = styled(SelectDetail).attrs((props) => ({
  isClearable: false,
  colorLabel: props.theme.colors['white'],
  fontSize: 15
}))``;

class Details extends Component {
  constructor(props) {
    super(props);
    const { selectedMarket } = props;
    const data = props.data;
    this.state = {
      showScroll: true,
      isScrolled: false,
      desc: false,
      viewport: {
        width: '100%',
        height: '100vh',
        latitude: 46.227638,
        longitude: 2.213749,
        zoom: 3
      },
      historyMarkets: [],
      selectedIndicator: get(
        this.getIndicatorOptions(selectedMarket, data),
        '',
        {}
      )
    };

    this.handleScroll = this.handleScroll.bind(this);
    this.myRef = React.createRef();
  }

  componentDidMount() {
    const { selectedMarket, data } = this.props;
    const { selectedIndicator } = this.state;
    const indicatorOptions = this.getIndicatorOptions(selectedMarket, data);

    if (isEmpty(indicatorOptions)) return;
    if (
      !some(indicatorOptions, (opts) => selectedIndicator.value === opts.value)
    ) {
      this.setState({ selectedIndicator: indicatorOptions[0] });
    }
  }

  componentWillUnmount() {
    this.myRef.current.removeEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedMarket, data, user } = this.props;
    const { selectedIndicator } = this.state;

    if (
      user.id !== null &&
      (user.language !== get(prevProps, 'user.language') ||
        user.isBranded !== get(prevProps, 'user.isBranded'))
    ) {
      this.props.getHeadersAndData();
    }

    if (
      Object.keys(data) &&
      Object.keys(data) !== Object.keys(prevProps.data)
    ) {
      const indicatorOptions = this.getIndicatorOptions(selectedMarket, data);

      if (isEmpty(indicatorOptions)) {
        return;
      }

      if (
        isEmpty(prevState.selectedIndicator) ||
        !some(
          indicatorOptions,
          (opts) => selectedIndicator.value === opts.value
        )
      ) {
        this.setState({ selectedIndicator: indicatorOptions[0] });
      }
    }
  }

  getDefaultIndicatorOptions() {
    return [
      {
        value: 'restaurants',
        label: I18n.t('synthesisDistribution.detail.networkOptions.restaurants')
      },
      {
        value: 'caves',
        label: I18n.t('synthesisDistribution.detail.networkOptions.caves')
      },
      {
        value: 'websites',
        label: I18n.t('synthesisDistribution.detail.networkOptions.websites')
      }
    ];
  }

  handleScroll(props) {
    this.setState({
      showScroll:
        props && props.path && props.path[0]
          ? props.path[0].scrollTop === 0 || props.path[0].scrollTop > 500
          : false,
      isScrolled:
        props && props.path && props.path[0]
          ? props.path[0].scrollTop > 0
          : false
    });
  }

  getIndicatorOptions(selectedMarket, data = {}) {
    const market = get(selectedMarket, 'value');
    const keys = Object.keys(data);
    const existingIndicatorOptions = this.getDefaultIndicatorOptions();
    data = { ...data, sites_web: null };

    return filter(
      existingIndicatorOptions,
      (opts) =>
        (market === 'all' || opts.value !== 'websites') &&
        some(keys, (key) => opts.value === key && !isEmpty(data[key]))
    );
  }

  renderMapOverlay() {
    let {
      isBranded,
      selectedZone,
      selectedMarket,
      selectedEntity,
      zoneOptions,
      marketsOptions,
      entitiesOptions,
      setZone,
      setMarket,
      setEntity
    } = this.props;

    let marketsOptionsWithDefault = [...marketsOptions].filter(
      (item) => item.label !== 'placeholderAll'
    );

    if (
      !marketsOptions.find((item) => item.label === I18n.t('placeholderAll'))
    ) {
      marketsOptionsWithDefault.unshift({
        value: 'all',
        label: I18n.t('placeholderAll')
      });
    }

    if (selectedMarket && selectedMarket.label === 'placeholderAll') {
      selectedMarket.label = I18n.t('placeholderAll');
    }

    return (
      <div className="synthesisDetailMapContainer">
        <div className="synthesisDetailOverlay">
          <div className="backToMap">
            <Link to="/distribution/synthesis">
              <div className="comeBackContainer">
                <div className="comeBackIcon">
                  <Icon icon="world" />
                </div>
                <h3>{I18n.t('synthesisDistribution.detail.goBack')}</h3>
              </div>
            </Link>
          </div>
          <div className="filtersContainer">
            <Select
              label={I18n.t('synthesisDistribution.filters.zone')}
              value={selectedZone}
              options={zoneOptions}
              onChange={setZone}
              isClearable={false}
              fontSize="20"
            />
            <Select
              label={I18n.t('synthesisDistribution.filters.market')}
              value={
                selectedMarket
                  ? {
                      ...selectedMarket,
                      label: selectedMarket.label
                    }
                  : null
              }
              options={marketsOptionsWithDefault.sort((a, b) =>
                a && b ? String(a.label).localeCompare(String(b.label)) : 0
              )}
              onChange={setMarket}
              isClearable={false}
              fontSize="20"
            />
            <Select
              label={
                isBranded
                  ? I18n.t('synthesisDistribution.filters.brand')
                  : I18n.t('synthesisDistribution.filters.wine')
              }
              value={selectedEntity}
              options={entitiesOptions}
              onChange={setEntity}
              isClearable={false}
              fontSize="20"
            />
          </div>
        </div>
      </div>
    );
  }

  renderHeader() {
    const { selectedEntity, selectedMarket } = this.props;
    const data = this.props.data;
    const { selectedIndicator } = this.state;
    const rank = get(data, `${selectedIndicator.value}.rank`);

    return (
      <div className="synthesisDetailHeader">
        <div className="synthesisDetailLeftHeader">
          <div className="titleContainer">
            <p className="wsLabel">
              {I18n.t('synthesisDistribution.detail.headerTitle')}
            </p>
            <SelectComponent
              className="titleType"
              colorLabel="black"
              colorValue="black"
              standardOptionsStyle={true}
              fontSize={15}
              minWidth=""
              display="flex"
              alignItems="center"
              value={selectedIndicator}
              options={this.getIndicatorOptions(selectedMarket, data)}
              onChange={(value) => this.setState({ selectedIndicator: value })}
            />
          </div>
          <div className="wsContainer">
            <p className="wsValue">{get(selectedEntity, 'label')}</p>
          </div>
        </div>
        <div className="synthesisDetailRightHeader">
          <p className="rankLabel">{I18n.t('rank')}</p>
          <h1 className="rankValue">{rank}</h1>
        </div>
      </div>
    );
  }

  renderIndicator(label, value) {
    if (!value) return null;
    return (
      <div className="indicatorContainer">
        <p className="indicatorLabel">{label}</p>
        <p className="indicatorValue">{value}</p>
      </div>
    );
  }

  getLeaderInfoDisplay({ dn, name }) {
    if (!name || !dn) return null;

    return (
      <React.Fragment>
        <span>{dn}</span> <span className="spanNameLeader">{name}</span>
      </React.Fragment>
    );
  }

  renderIndicators() {
    const { data, user } = this.props;
    const { selectedIndicator } = this.state;
    const {
      dn,
      tp,
      avgPrice,
      newStores,
      panelEvolution,
      displayedCurrency = 'EUR',
      lostStores,
      leaderWine = {}
    } = get(data, `${selectedIndicator.value}`, {});
    const avgPriceDisplay = avgPrice
      ? avgPrice.toLocaleString('fr-FR', {
          style: 'currency',
          currency: displayedCurrency ? displayedCurrency : 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
      : null;

    let panelEvolutionStr = '';

    if (!isNaN(Number(panelEvolution))) {
      let panelEvolutionInt = Number(panelEvolution);
      let sign = '';
      if (panelEvolutionInt < 0) {
        panelEvolutionInt = -1 * panelEvolutionInt;
        sign = '-';
      } else {
        sign = '+';
      }

      panelEvolutionStr = sign + panelEvolutionInt;
    }
    const winLossEvolution =
      !isNaN(Number(newStores)) &&
      !isNaN(Number(lostStores)) &&
      !isNaN(Number(panelEvolution))
        ? `${Number(newStores)}/${Number(lostStores)}/${panelEvolutionStr}`
        : null;
    return (
      <div className="synthesisContentIndicators">
        <div className="synthesisContentIndicatorsLeft">
          <div className="bigIndicator">
            <p className="bigIndicatorLabel">
              {get(selectedIndicator, 'value') === 'websites'
                ? I18n.t('websites')
                : selectedIndicator.label}
            </p>
            <p className="bigIndicatorValue">{dn | 0}</p>
          </div>
          <div className="actionButtonContainer">
            {get(selectedIndicator, 'value') === 'websites' ? (
              <Link to="/distribution/online-sales/overview">
                <IconButton icon="eye2" />
              </Link>
            ) : (
              <Link to="/distribution/restaurants">
                <IconButton icon="eye2" />
              </Link>
            )}
          </div>
        </div>
        <div className="synthesisContentIndicatorsRight">
          {this.renderIndicator(
            I18n.t('synthesisDistribution.detail.indicators.tp'),
            tp
          )}
          {this.renderIndicator(
            user.isBranded
              ? I18n.t('averagePrice')
              : I18n.t('synthesisDistribution.detail.indicators.price'),
            avgPriceDisplay
          )}
          {get(selectedIndicator, 'value') !== 'websites' &&
            this.renderIndicator(
              I18n.t('synthesisDistribution.detail.indicators.posCount'),
              winLossEvolution
            )}
          {this.renderIndicator(
            I18n.t('synthesisDistribution.detail.indicators.leader'),
            this.getLeaderInfoDisplay(leaderWine)
          )}
        </div>
      </div>
    );
  }

  getExportXlsOptions() {
    const { isBranded, selectedMarket, selectedZone } = this.props;
    let options = {
      market_name: get(selectedMarket, 'value'),
      region: get(selectedZone, 'value')
    };

    if (isBranded) {
      return {
        ...options,
        brands: JSON.stringify([get(this.props, 'selectedEntity.label')])
      };
    }

    return {
      ...options,
      wines: JSON.stringify([get(this.props, 'selectedEntity.label')])
    };
  }

  renderRightContent() {
    const { selectedMarket, selectedEntity } = this.props;
    const data = this.props.data;
    const { selectedIndicator } = this.state;
    const chartData = get(data, `${selectedIndicator.value}.chartData`, []);
    const tableData = get(data, `${selectedIndicator.value}.tableData`, []);
    const historyMarkets = get(data, `${selectedIndicator.value}.markets`, [])
      .map((item) => item.name)
      .join(', ');

    const optionsExportXls = this.getExportXlsOptions(chartData);

    return (
      <div className="synthesisContentContainer">
        {this.renderHeader()}
        <div id="containerScroll" ref={this.myRef} className="synthesisContent">
          {this.renderIndicators()}
          {get(selectedMarket, 'value') === 'all' && (
            <TableComponent
              export={this.props}
              data={tableData}
              selectedIndicator={selectedIndicator}
              optionsExportXls={optionsExportXls}
            />
          )}
          <CompareLineGraph
            type={selectedIndicator ? selectedIndicator.value : 'restaurants'}
            clientWines={[selectedEntity]}
            data={chartData}
            getFirstForExport={(value) =>
              this.setState({ rivalToExport: value })
            }
            selectedIndicator={selectedIndicator}
            historyMarkets={historyMarkets}
            optionsExportXls={optionsExportXls}
          />
        </div>
      </div>
    );
  }

  render() {
    const { loading } = this.props;
    return (
      <DetailsContainer>
        <ReactMapGL
          {...this.state.viewport}
          style={{ position: 'fixed' }}
          mapStyle="mapbox://styles/waves59/cjwiz4hy6003w1cnx8q647swr/draft"
          mapboxAccessToken="pk.eyJ1Ijoid2F2ZXM1OSIsImEiOiJjanNnMGQ4ZG4wMW90M3lrZ2g5emRsdHo1In0.zMQjOGNuUxxsdlEUydihgg"
        />
        {this.renderMapOverlay()}
        {this.renderRightContent()}
        <Spinner loading={loading} />
      </DetailsContainer>
    );
  }
}

const Index = () => {
  const { user } = useUserMe();
  const [state, setState] = useState({
    zone: 'world',
    market: 'all',
    entity: null
  });

  const { isLoadingFilters, filters } = useDistributionSynthesisFilters();

  const { isLoadingData, data } = useDistributionSynthesisQuery(
    {
      wine: !user.isBranded ? state.entity : null,
      brand: user.isBranded ? state.entity : null,
      market: state.market !== 'all' ? state.market : null,
      region: state.zone !== 'world' ? state.zone : null
    },
    state.entity !== null
  );

  const entities = useMemo(() => {
    return user.isBranded ? filters.clientBrands : filters.clientWines;
  }, [filters, user.isBranded]);

  useEffect(() => {
    if (entities.length > 0) {
      setState((prevState) => ({ ...prevState, entity: entities[0] }));
    }
  }, [entities]);

  const setZone = useCallback((zone) => {
    setState((prevState) => ({ ...prevState, zone }));
  }, []);

  const setMarket = useCallback((market) => {
    setState((prevState) => ({ ...prevState, market }));
  }, []);

  const setEntity = useCallback((entity) => {
    setState((prevState) => ({ ...prevState, entity }));
  }, []);

  const defaultRegionOption = { value: 'world', label: I18n.t('world') };
  const zoneOptions = [defaultRegionOption, ...toOptionsList(filters.zones)];
  const marketsOptionsByZone = reduce(
    filters.zones,
    (acc, key) => {
      acc[key] = toOptionsList(filters.marketsByZone[key], 'name');
      return acc;
    },
    {}
  );

  const marketsOptions = getMarketsForRegion(state.zone, marketsOptionsByZone);
  const entitiesOptions = toOptionsList(entities);

  return (
    <Details
      user={user}
      isBranded={user.isBranded}
      selectedZone={zoneOptions.find((option) => option.value === state.zone)}
      selectedMarket={marketsOptions.find(
        (option) => option.value === state.market
      )}
      selectedEntity={entitiesOptions.find(
        (option) => option.value === state.entity
      )}
      zoneOptions={zoneOptions}
      marketsOptions={marketsOptions}
      entitiesOptions={entitiesOptions}
      setZone={(e) => setZone(e.value)}
      setMarket={(e) => setMarket(e.value)}
      setEntity={(e) => setEntity(e.value)}
      data={data}
      loading={isLoadingFilters || isLoadingData}
    />
  );
};

export default Index;
